import { MetricTypes } from 'common/constants/metric-types';
import { MetricAttribution } from 'common/types/metric.models';
import {
  AxisChartByString,
  ChartDataType,
  ChartDisplayType,
  DataPointSetByString
} from 'features/charts/chart.models';
import { v4 as guid } from 'uuid';
import { MetricReport, MetricValue } from './types/metric-report';
import { getMetricTypeFriendlyName } from './functions/metric-type-functions';
import {
  calculateCpaBase,
  calculateCps,
  calculateCpsBase,
  calculateRoasBase,
  calculateRoiBase
} from './functions/metric-calculation-functions';

export const metricAttributionsToChartByString = (
  metrics: MetricAttribution[],
  selectedAttributionModel: string,
  searchedTerm: string
): AxisChartByString => {
  const searchedTermUpper = searchedTerm.toUpperCase();

  const dataPointSets: DataPointSetByString[] = metrics
    .filter(
      (metric) =>
        metric.attributionModelName === selectedAttributionModel &&
        metric.metricTypeName.toUpperCase().includes(searchedTermUpper)
    )
    .map(toDataPointSetByString);

  const chart: AxisChartByString = {
    id: guid(),
    title: '',
    type: ChartDataType.STRING,
    defaultChartDisplayType: ChartDisplayType.STACKED_BAR,
    shouldShowChartTypeDropdown: false,
    yAxisFormat: new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }),
    tooltipFormat: new Intl.NumberFormat('en-GB', {
      maximumFractionDigits: 2
    }),
    xAxisTitle: '',
    yAxisTitle: '',
    dataPointSets,
    shouldAnimate: true
  };

  return chart;

  function toDataPointSetByString(
    metric: MetricAttribution
  ): DataPointSetByString {
    const dataPoint: DataPointSetByString = {
      dataPointString: metric.metricTypeName,
      dataPointSetName: getMetricTypeFriendlyName(metric.metricType),
      value: metric.attributionValue
    };

    return dataPoint;
  }
};

export const metricAttributionsToMetricReports = (
  metrics: MetricAttribution[],
  primaryAttributionModel: string,
  compareAttributionModel?: string
): MetricReport[] => {
  const primaryMetrics = metrics.filter(
    (metric) => metric.attributionModelName === primaryAttributionModel
  );

  const metricReports = primaryMetrics.reduce<MetricReport[]>(
    (allReports, metric) => {
      const existingReport = allReports.find(
        (m) => m.Field === metric.metricTypeName
      );

      if (existingReport) {
        existingReport[metric.metricType] = {
          main: metric.attributionValue,
          compare: 0
        } as MetricValue;
      } else {
        const newReport: MetricReport = {
          Field: metric.metricTypeName,
          [metric.metricType]: {
            main: metric.attributionValue,
            compare: 0
          } as MetricValue
        };
        allReports = [...allReports, newReport];
      }

      return allReports;
    },
    []
  );

  const compareMetrics = metrics.filter(
    (metric) => metric.attributionModelName === compareAttributionModel
  );

  for (const compareMetric of compareMetrics) {
    const existingMetricReportIndex = metricReports.findIndex(
      (report) => report.Field === compareMetric.metricTypeName
    );

    if (existingMetricReportIndex >= 0) {
      let existingMetricReport = metricReports[existingMetricReportIndex];
      let existingMetricValue = existingMetricReport[
        compareMetric.metricType
      ] as MetricValue;

      existingMetricValue = {
        ...existingMetricValue,
        compare: compareMetric.attributionValue
      };

      existingMetricReport = metricReports[existingMetricReportIndex] = {
        ...metricReports[existingMetricReportIndex],
        [compareMetric.metricType]: existingMetricValue
      };
    } else {
      const newMetric: MetricReport = {
        Field: compareMetric.metricTypeName,
        [compareMetric.metricType]: {
          main: 0,
          compare: compareMetric.attributionValue
        } as MetricValue
      };
      metricReports.push(newMetric);
    }
  }

  metricReports.forEach((metric) => {
    // refactor all below
    setMetricTypesToZeroWhichHaveNoValue(metric);

    const cost = metric[MetricTypes.COST] as MetricValue;
    const forms = metric[MetricTypes.WEB] as MetricValue;
    const calls = metric[MetricTypes.INBOUND_CALL] as MetricValue;
    const revenue = metric[MetricTypes.REVENUES] as MetricValue;
    const revenueValue = metric[MetricTypes.REVENUE_VALUES] as MetricValue;

    const mainCostValue = (cost.main as number) ?? 0;
    const mainFormsValue = (forms.main as number) ?? 0;
    const mainCallsValue = (calls.main as number) ?? 0;
    const mainRevenueValue = (revenue.main as number) ?? 0;
    const mainRevenueValuesValue = (revenueValue.main as number) ?? 0;

    const compareCostValue = (cost.compare as number) ?? 0;
    const compareFormsValue = (forms.compare as number) ?? 0;
    const compareCallsValue = (calls.compare as number) ?? 0;
    const compareRevenueValue = (revenue.compare as number) ?? 0;
    const compareRevenueValuesValue = (revenueValue.compare as number) ?? 0;

    metric[MetricTypes.CPA] = {
      main: calculateCpaBase(mainCostValue, mainFormsValue, mainCallsValue),
      compare: calculateCpaBase(
        compareCostValue,
        compareFormsValue,
        compareCallsValue
      )
    } as MetricValue;
    metric[MetricTypes.CPS] = {
      main: calculateCpsBase(mainCostValue, mainRevenueValue),
      compare: calculateCpsBase(compareCostValue, compareRevenueValue)
    } as MetricValue;
    metric[MetricTypes.ROI] = {
      main: calculateRoiBase(mainCostValue, mainRevenueValuesValue),
      compare: calculateRoiBase(compareCostValue, compareRevenueValuesValue)
    } as MetricValue;
    metric[MetricTypes.ROAS] = {
      main: calculateRoasBase(mainCostValue, mainRevenueValuesValue),
      compare: calculateRoasBase(compareCostValue, compareRevenueValuesValue)
    } as MetricValue;
  });

  return metricReports;
};

// DO NOT DELETE. This is necessary for the sorting function to work in TanStack
// A property must have a default value of 0, not undefined
function setMetricTypesToZeroWhichHaveNoValue(metric: MetricReport): void {
  metric[MetricTypes.CLICKS] =
    metric[MetricTypes.CLICKS] ??
    ({
      main: 0,
      compare: 0
    } as MetricValue);
  metric[MetricTypes.IMPRESSIONS] =
    metric[MetricTypes.IMPRESSIONS] ??
    ({
      main: 0,
      compare: 0
    } as MetricValue);
  metric[MetricTypes.INBOUND_CALL] =
    metric[MetricTypes.INBOUND_CALL] ??
    ({
      main: 0,
      compare: 0
    } as MetricValue);
  metric[MetricTypes.WEB] =
    metric[MetricTypes.WEB] ??
    ({
      main: 0,
      compare: 0
    } as MetricValue);
  metric[MetricTypes.OPPORTUNITIES] =
    metric[MetricTypes.OPPORTUNITIES] ??
    ({
      main: 0,
      compare: 0
    } as MetricValue);
  metric[MetricTypes.OPPORTUNITY_VALUES] =
    metric[MetricTypes.OPPORTUNITY_VALUES] ??
    ({
      main: 0,
      compare: 0
    } as MetricValue);
  metric[MetricTypes.REVENUES] =
    metric[MetricTypes.REVENUES] ??
    ({
      main: 0,
      compare: 0
    } as MetricValue);
  metric[MetricTypes.REVENUE_VALUES] =
    metric[MetricTypes.REVENUE_VALUES] ??
    ({
      main: 0,
      compare: 0
    } as MetricValue);
  metric[MetricTypes.COST] =
    metric[MetricTypes.COST] ??
    ({
      main: 0,
      compare: 0
    } as MetricValue);
}

export const createTotalsMetricReport = (
  metricReports: MetricReport[]
): MetricReport => {
  const web = metricReports
    .map((mr) => ((mr[MetricTypes.WEB] as MetricValue).main as number) ?? 0)
    .reduce((a, b) => a + b, 0);

  const inboundCall = metricReports
    .map(
      (mr) =>
        ((mr[MetricTypes.INBOUND_CALL] as MetricValue).main as number) ?? 0
    )
    .reduce((a, b) => a + b, 0);

  const clicks = metricReports
    .map((mr) => ((mr[MetricTypes.CLICKS] as MetricValue).main as number) ?? 0)
    .reduce((a, b) => a + b, 0);

  const impressions = metricReports
    .map(
      (mr) => ((mr[MetricTypes.IMPRESSIONS] as MetricValue).main as number) ?? 0
    )
    .reduce((a, b) => a + b, 0);

  const cost = metricReports
    .map((mr) => ((mr[MetricTypes.COST] as MetricValue).main as number) ?? 0)
    .reduce((a, b) => a + b, 0);

  const revenues = metricReports
    .map(
      (mr) => ((mr[MetricTypes.REVENUES] as MetricValue).main as number) ?? 0
    )
    .reduce((a, b) => a + b, 0);

  const revenueValues = metricReports
    .map(
      (mr) =>
        ((mr[MetricTypes.REVENUE_VALUES] as MetricValue).main as number) ?? 0
    )
    .reduce((a, b) => a + b, 0);

  const opportunities = metricReports
    .map(
      (mr) =>
        ((mr[MetricTypes.OPPORTUNITIES] as MetricValue).main as number) ?? 0
    )
    .reduce((a, b) => a + b, 0);

  const opportunityValues = metricReports
    .map(
      (mr) =>
        ((mr[MetricTypes.OPPORTUNITY_VALUES] as MetricValue).main as number) ??
        0
    )
    .reduce((a, b) => a + b, 0);

  const cpa = calculateCpaBase(cost, web, inboundCall);

  const cps = calculateCpsBase(cost, revenues);

  const roi = calculateRoiBase(cost, revenueValues);

  const roas = calculateRoasBase(cost, revenueValues);

  const compareWeb = metricReports
    .map((mr) => ((mr[MetricTypes.WEB] as MetricValue).compare as number) ?? 0)
    .reduce((a, b) => a + b, 0);

  const compareInboundCall = metricReports
    .map(
      (mr) =>
        ((mr[MetricTypes.INBOUND_CALL] as MetricValue).compare as number) ?? 0
    )
    .reduce((a, b) => a + b, 0);

  const compareClicks = metricReports
    .map(
      (mr) => ((mr[MetricTypes.CLICKS] as MetricValue).compare as number) ?? 0
    )
    .reduce((a, b) => a + b, 0);

  const compareImpressions = metricReports
    .map(
      (mr) =>
        ((mr[MetricTypes.IMPRESSIONS] as MetricValue).compare as number) ?? 0
    )
    .reduce((a, b) => a + b, 0);

  const compareCost = metricReports
    .map((mr) => ((mr[MetricTypes.COST] as MetricValue).compare as number) ?? 0)
    .reduce((a, b) => a + b, 0);

  const compareRevenues = metricReports
    .map(
      (mr) => ((mr[MetricTypes.REVENUES] as MetricValue).compare as number) ?? 0
    )
    .reduce((a, b) => a + b, 0);

  const compareRevenueValues = metricReports
    .map(
      (mr) =>
        ((mr[MetricTypes.REVENUE_VALUES] as MetricValue).compare as number) ?? 0
    )
    .reduce((a, b) => a + b, 0);

  const compareOpportunities = metricReports
    .map(
      (mr) =>
        ((mr[MetricTypes.OPPORTUNITIES] as MetricValue).compare as number) ?? 0
    )
    .reduce((a, b) => a + b, 0);

  const compareOpportunityValues = metricReports
    .map(
      (mr) =>
        ((mr[MetricTypes.OPPORTUNITY_VALUES] as MetricValue)
          .compare as number) ?? 0
    )
    .reduce((a, b) => a + b, 0);

  const compareCpa = calculateCpaBase(
    compareCost,
    compareWeb,
    compareInboundCall
  );

  const compareCps = calculateCpsBase(compareCost, compareRevenues);

  const compareRoi = calculateRoiBase(compareCost, compareRevenueValues);

  const compareRoas = calculateRoasBase(compareCost, compareRevenueValues);

  const totalsMetricReport: MetricReport = {
    Field: 'Total',
    [MetricTypes.WEB]: { main: web, compare: compareWeb } as MetricValue,
    [MetricTypes.INBOUND_CALL]: {
      main: inboundCall,
      compare: compareInboundCall
    } as MetricValue,
    [MetricTypes.IMPRESSIONS]: {
      main: impressions,
      compare: compareImpressions
    } as MetricValue,
    [MetricTypes.CLICKS]: {
      main: clicks,
      compare: compareClicks
    } as MetricValue,
    [MetricTypes.COST]: { main: cost, compare: compareCost } as MetricValue,
    [MetricTypes.REVENUES]: {
      main: revenues,
      compare: compareRevenues
    } as MetricValue,
    [MetricTypes.REVENUE_VALUES]: {
      main: revenueValues,
      compare: compareRevenueValues
    } as MetricValue,
    [MetricTypes.OPPORTUNITIES]: {
      main: opportunities,
      compare: compareOpportunities
    } as MetricValue,
    [MetricTypes.OPPORTUNITY_VALUES]: {
      main: opportunityValues,
      compare: compareOpportunityValues
    } as MetricValue,
    [MetricTypes.CPA]: { main: cpa, compare: compareCpa } as MetricValue,
    [MetricTypes.CPS]: { main: cps, compare: compareCps } as MetricValue,
    [MetricTypes.ROI]: { main: roi, compare: compareRoi } as MetricValue,
    [MetricTypes.ROAS]: { main: roas, compare: compareRoas } as MetricValue
  };

  return totalsMetricReport;
};
